import React from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue
} from "react-hook-form";
import { Link } from "react-router-dom";
import { mdiAlertOutline, mdiCheckDecagramOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import {
  ClipboardClockSVGIcon,
  CloudDownloadSVGIcon,
  DataCheckSVGIcon,
  DollarCircleSvgIcon,
  InvoicingSVGIcon,
  LoadingSpinnerSVG,
  ManagePayIcon,
  NoInvoicingSVGIcon,
  SummarizeSvgIcon,
  TruckSVGIcon,
  WalletSvgIcon
} from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import { formatNumber, numberFormatter } from "helpers/formatNumber";
import { GridCardItem } from "models/Base";
import { IStepItem } from "models/Component";
import { IOption, ITabSwitch } from "models/Form";
import { IFactoringPaymentProfile, IPaymentSpeed, ITableHeaderList } from "models/HaulPay";
import { COLOR_BY_STATUS, COLOR_BY_STATUS_FOR_MY_CARRIER } from "./base";
import { routes, UPLOAD_DOCUMENT_URLS } from "./routes";
import NOAStatus from "components/NOAStatus";
import PaymentSpeedInput from "../components/PaymentSpeedInput";
import { ICarriersFormData } from "../features/main/HaulPay/Broker/MyCarriers/Origin";
import styles from "../features/main/HaulPay/Broker/MyCarriers/Origin/style.module.scss";
import { useModal } from "../context/ModalContext";
import UploadDocumentsButton from "../components/UploadDocumentsButton";
import DocumentStatusIcon from "../components/DocumentStatusIcon";
import {
  FUNDING_REQUEST_CONTRACT_TYPES_MAP,
  FundingRequestContractType
} from "../helpers/constantTypes";

export const BROKER_ITEMS: GridCardItem[] = [
  {
    id: 0,
    label: "Manage Pay",
    icon: (props: React.SVGProps<SVGSVGElement>) => <ManagePayIcon {...props} />,
    link: routes.HAULPAY_BROKER_MANAGE_PAY
  },
  {
    id: 1,
    label: "My Carriers",
    icon: (props: React.SVGProps<SVGSVGElement>) => <TruckSVGIcon {...props} />,
    link: routes.HAULPAY_BROKER_CARRIERS_ORIGIN
  },
  {
    id: 2,
    label: "My Wallet",
    icon: (props: React.SVGProps<SVGSVGElement>) => <WalletSvgIcon {...props} />,
    link: routes.HAULPAY_BROKER_WALLET_PENDING
  },
  {
    id: 3,
    label: "Reports",
    icon: (props: React.SVGProps<SVGSVGElement>) => <SummarizeSvgIcon {...props} />,
    link: routes.HAULPAY_BROKER_REPORTS
  },
  {
    id: 4,
    label: "Credit",
    icon: (props: React.SVGProps<SVGSVGElement>) => <DollarCircleSvgIcon {...props} />,
    link: routes.HAULPAY_CREDIT
  }
];

export const BROKER_TAB_ITEMS: ITabSwitch[] = [
  {
    id: 0,
    label: "Pending Transactions",
    value: "pending-transactions",
    link: routes.HAULPAY_BROKER_WALLET_PENDING
  },
  {
    id: 1,
    label: "Statement History",
    value: "statement-history",
    link: routes.HAULPAY_BROKER_WALLET_HISTORY
  }
];

export const REPORTS_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "action",
    label: "Actions",
    render: (item: {
      downloadReport: VoidFunction;
      isFetching: boolean;
      id: string;
      selectedId: string;
    }) => (
      <button type="button" className="!ml-4" onClick={item.downloadReport}>
        {item.isFetching && item.selectedId === item.id ? (
          <LoadingSpinnerSVG className="inline size-3 animate-spin text-text fill-primary mx-auto" />
        ) : (
          <CloudDownloadSVGIcon className="flex-none my-auto text-info" />
        )}
      </button>
    )
  },
  {
    value: "created",
    label: "Requested On",
    render: (item: string) => <span>{dayjs(item).format("MMM D, YYYY h:ss A")}</span>
  },
  {
    value: "reportType",
    label: "Type",
    render: (item: string) => <span className="capitalize">{item}</span>
  },
  {
    value: "details",
    label: "Date Range"
  }
];

export const WALLET_PENDING_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "created",
    label: "Date",
    render: (item: string) => {
      return dayjs(item).format("MM/DD/YYYY");
    }
  },
  {
    value: "description",
    label: "Description"
  },
  {
    value: "invoice_number",
    label: "Invoice"
  },
  {
    value: "credit_amount",
    label: "Credit",
    render: (item: number) => formatNumber(item, 2)
  },
  {
    value: "debit_amount",
    label: "Debit",
    render: (item: number) => formatNumber(item, 2)
  }
];

export const WALLET_HISTORY_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "created",
    label: "Date",
    render: (item: string) => {
      return dayjs(item).format("MM/DD/YYYY");
    }
  },
  {
    value: "description",
    label: "Description"
  },
  {
    value: "invoice_number",
    label: "Invoice"
  },
  {
    value: "credit_amount",
    label: "Credit",
    render: (item: number) => formatNumber(item, 2)
  },
  {
    value: "debit_amount",
    label: "Debit",
    render: (item: number) => formatNumber(item, 2)
  },
  {
    value: "wallet_balance",
    label: "Balance",
    render: (item: number) => formatNumber(item, 2)
  }
];

export const FR_CONTRACT_TYPE = [
  {
    id: 0,
    label: "Factored",
    value: "STD_BROKER"
  },
  {
    id: 1,
    label: "Non-Factored Pay Carrier",
    value: "NON_FACTORED_STANDARD"
  },
  {
    id: 2,
    label: "Non-Factored Bill Out Only",
    value: "NON_FACTORED_BILL_OUT"
  },
  {
    id: 3,
    label: "Self-Financed No Invoicing",
    value: "SELF_FINANCE_NO_INVOICE"
  },
  {
    id: 4,
    label: "Self-Financed With Invoicing",
    value: "SELF_FINANCE_WITH_INVOICE"
  }
];

export const ORIGIN_CARRIER_SEARCH_FILTER_LIST = [
  {
    id: 0,
    label: "Carrier Name",
    value: "carrier_dba"
  },
  {
    id: 1,
    label: "Status",
    value: "status"
  },
  {
    id: 2,
    label: "Email",
    value: "email"
  },
  {
    id: 3,
    label: "MC #",
    value: "mc"
  },
  {
    id: 4,
    label: "DOT #",
    value: "dot"
  },
  {
    id: 5,
    label: "Phone",
    value: "phone"
  }
];

export const ORIGIN_CARRIER_STATUS_FILTER_LIST = [
  {
    id: 0,
    label: "Pending",
    value: "pending"
  },
  {
    id: 1,
    label: "Needs Review",
    value: "needs_review"
  },
  {
    id: 2,
    label: "Approved",
    value: "approved"
  },
  {
    id: 3,
    label: "Declined",
    value: "declined"
  }
];

export const INVITED_CARRIER_SEARCH_FILTER_LIST = [
  {
    id: 0,
    label: "Email",
    value: "email"
  },
  {
    id: 1,
    label: "Phone Number",
    value: "phone_number"
  }
];

export const EQUIPMENT_TYPE_LIST = [
  {
    id: 0,
    label: "Other",
    value: "other"
  }
];

export const FIRST_PICK_UP_LIST = [
  {
    id: 0,
    label: "Anywhere",
    value: "anywhere"
  }
];

export const CREATE_WITH_MARGIN_ADVANCE = [
  {
    id: 0,
    label: "Yes",
    value: "yes"
  },
  {
    id: 1,
    label: "No",
    value: "no"
  }
];

export const PERCENTAGE_LIST = [
  {
    id: 0,
    label: "0%",
    value: "0",
    amount: 0
  },
  {
    id: 1,
    label: "50%",
    value: "50",
    amount: 50
  },
  {
    id: 2,
    label: "100%",
    value: "100",
    amount: 100
  }
];

export const UPLOAD_TYPE_LIST = [
  {
    id: 1,
    label: "HaulPay Upload",
    value: "haulpay_upload"
  }
];

export const VISIBILITY_LIST: IOption[] = [
  { id: 0, label: "Carrier Only", value: "carrier" },
  { id: 1, label: "Shipper/Debtor Only", value: "shipper,debtor" },
  { id: 2, label: "Carrier & Shipper/Debtor", value: "carrier,shipper,debtor" }
];

const VISIBILITY_MAP = (visibility: string[]): string | undefined => {
  if (JSON.stringify(visibility) === JSON.stringify(["carrier"])) {
    return "Carrier Only";
  } else if (JSON.stringify(visibility) === JSON.stringify(["shipper", "debtor"])) {
    return "Shipper/Debtor Only";
  } else if (JSON.stringify(visibility) === JSON.stringify(["carrier", "shipper", "debtor"])) {
    return "Carrier & Shipper/Debtor";
  }
};

export const REPORTS_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "date",
    label: "Transaction Date"
  },
  {
    value: "invoice",
    label: "Invoice #"
  },
  {
    value: "debitAmount",
    label: "Debit Amount"
  },
  {
    value: "creditAmount",
    label: "Credit Amount"
  },
  {
    value: "description",
    label: "Description"
  }
];

export const BROKER_MY_CARRIER_TAB_ITEMS: ITabSwitch[] = [
  {
    id: 0,
    label: "My Carriers",
    value: "carriers",
    link: routes.HAULPAY_BROKER_CARRIERS_ORIGIN
  },
  {
    id: 1,
    label: "Invited Carriers",
    value: "invited-carriers",
    link: routes.HAULPAY_BROKER_INVITED_CARRIER
  }
];

export const PAYMENT_SPEED_LIST = [
  {
    id: 0,
    label: "1 Day",
    value: "1"
  },
  {
    id: 7,
    label: "7 Days",
    value: "7"
  },
  {
    id: 15,
    label: "15 Days",
    value: "15"
  },
  {
    id: 21,
    label: "21 Days",
    value: "21"
  },
  {
    id: 30,
    label: "30 Days",
    value: "30"
  }
];

export const PORTION_PERCENT_LIST = [
  {
    id: 0,
    label: "0%",
    value: "0"
  },
  {
    id: 50,
    label: "50%",
    value: "50"
  },
  {
    id: 100,
    label: "100%",
    value: "100"
  }
];

export const PAY_TAB_LIST = [
  {
    id: 0,
    label: "All",
    value: "all"
  },
  {
    id: 1,
    label: "In Process",
    value: "in_process"
  },
  {
    id: 2,
    label: "Pending",
    value: "pending"
  },
  {
    id: 3,
    label: "Incomplete",
    value: "incomplete"
  },
  {
    id: 4,
    label: "Pending",
    value: "special_pending"
  },
  {
    id: 5,
    label: "Incomplete",
    value: "pending_delivery"
  },
  {
    id: 6,
    label: "Document Review",
    value: "document_review"
  },
  {
    id: 7,
    label: "Non Factored",
    value: "non_factored"
  },
  {
    id: 8,
    label: "Paid",
    value: "paid"
  },
  {
    id: 9,
    label: "Document Issue",
    value: "document_issue"
  },
  {
    id: 10,
    label: "Fuel Advance",
    value: "fuel_advance"
  },
  {
    id: 11,
    label: "Approved",
    value: "approved"
  },
  {
    id: 12,
    label: "Declined",
    value: "declined"
  },
  {
    id: 13,
    label: "Carrier Paid",
    value: "carrier_paid"
  },
  {
    id: 14,
    label: "Invoice Paid",
    value: "invoice_paid"
  },
  {
    id: 15,
    label: "Pending Originals",
    value: "pending_originals"
  }
];

export const PAY_STATUS_LIST = [
  {
    id: 0,
    label: "Incomplete",
    value: "Pending Delivery"
  },
  {
    id: 1,
    label: "Pending",
    value: "Special Pending"
  },
  {
    id: 2,
    label: "Declined",
    value: "Declined"
  },
  {
    id: 3,
    label: "Document Issue",
    value: "Document Issue"
  },
  {
    id: 4,
    label: "Approved",
    value: "Remote Approved"
  }
];

export const FR_STATUS = [
  {
    id: 0,
    value: "Pending Delivery",
    label: "Incomplete"
  },
  {
    id: 1,
    value: "Hold",
    label: "Hold"
  },
  {
    id: 2,
    value: "Special Pending",
    label: "Pending"
  },
  {
    id: 3,
    value: "Declined",
    label: "Declined"
  },
  {
    id: 4,
    value: "Document Issue",
    label: "Document Issue"
  },
  {
    id: 5,
    value: "Remote Approved",
    label: "Approved"
  },
  {
    id: 6,
    value: "NON_FACTORED_BILL_OUT",
    label: "Non-Factored Bill Out Only"
  },
  {
    id: 7,
    value: "NON_FACTORED_STANDARD",
    label: "Non-Factored Pay carrier"
  },
  {
    id: 8,
    value: "paid",
    label: "Paid"
  }
];

interface File {
  id: string;
  filename: string;
  downloadUrl: string;
  category: string;
  visibleTo: string[];
}

interface FileTooltipProps {
  files: File[];
}

export const MANAGE_PAY_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: ["status", "id", "attachments", "factoring", "contractType"],
    label: "Status",
    render: (item: any) => {
      const { openModal, closeModal } = useModal();
      const fileCategories = item.attachments.flatMap((file: File) =>
        file.category
          .split(",")
          .map((cat) => cat.trim())
          .filter(Boolean)
      );
      const FileTooltip: React.FC<FileTooltipProps> = ({ files }) => {
        return (
          <div className="space-y-4">
            <div
              className="p-4 rounded shadow hover:bg-gray-900 transition duration-200"
              onClick={() => handleUploadAttachment(item?.id)}>
              <UploadDocumentsButton
                onClick={() => handleUploadAttachment(item?.id)}
                className="m-2"
              />
            </div>
            {files.map((file) => (
              <div
                key={file.id}
                className="borderp-4 rounded shadow hover:bg-gray-900 transition duration-200">
                <div className="mb-2">
                  <a
                    href={file.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold hover:underline">
                    {file.filename}
                  </a>
                </div>
                <div className="text-sm text-gray-600">{VISIBILITY_MAP(file.visibleTo)}</div>
                <div className="text-sm text-gray-600">
                  {file.category || "No category available"}
                </div>
              </div>
            ))}
          </div>
        );
      };

      const handleUploadAttachment = (id: string) =>
        openModal("upload-documents", {
          title: "Upload New Documents",
          onClose: () => closeModal("upload-documents"),
          urlCallback: UPLOAD_DOCUMENT_URLS({
            factoring_id: item?.factoring?.id,
            funding_request_id: id
          }).FR_ATTACHMENT,
          dropzoneProps: {
            visibility: true
          },
          isFundingRequest: true,
          fundingRequestId: item.id
        });

      return (
        <div className="flex flex-row gap-2.5">
          <Link
            to={`${routes.HAULPAY_BROKER_FUNDING_REQUEST}/${item?.id}`}
            className={clsx(
              "rounded-3xl justify-center px-3.5 py-1.5 text-xs flex items-center gap-1.5 border",
              COLOR_BY_STATUS[item?.status]
            )}>
            <ClipboardClockSVGIcon className="flex-none size-5" />
            <span>{item?.status}</span>
          </Link>
          <Tooltip position="down" message={<FileTooltip files={item.attachments} />}>
            <DocumentStatusIcon
              documentTypes={fileCategories}
              contractType={item?.contractType}
              status={item?.status}
            />
          </Tooltip>
        </div>
      );
    }
  },
  {
    value: "warning",
    label: " ",
    isShow: false,
    render: () => (
      <Tooltip message="You can specify the text here for different kinds of error.">
        <Icon path={mdiAlertOutline} className="text-error" />
      </Tooltip>
    )
  },
  {
    value: "contractType",
    label: "Type",
    render: (contractType: FundingRequestContractType) => (
      <div>
        <p className="capitalize">{FUNDING_REQUEST_CONTRACT_TYPES_MAP[contractType]}</p>
      </div>
    )
  },
  {
    value: "debtor.name",
    label: "Debtor",
    rowStyle: "!w-44 !break-words"
  },
  {
    value: "paymentProfile.companyName",
    label: "Carrier"
  },
  {
    value: ["factoringDebtorRelation", "factoringId", "debtor.id"],
    label: "NOA Status",
    render: (items: any) => (
      <NOAStatus
        {...items?.factoringDebtorRelation}
        debtorId={items?.id}
        factoringId={items?.factoringId}
      />
    )
  },
  {
    value: ["amount", "contractType", "debtor.name"],
    label: "Invoice Amt.",
    render: (item: any) => (
      <div className="flex items-center gap-0.5">
        <span>{formatNumber(item?.amount, 2)}</span>
        {item?.contractType === "SELF_FINANCE_NO_INVOICE" || item?.name === "No Debtor" ? (
          <Tooltip message="Self-Finance No Invoice">
            <NoInvoicingSVGIcon className="size-6 text-approvedBorder" />
          </Tooltip>
        ) : item?.contractType === "SELF_FINANCE_WITH_INVOICE" ? (
          <Tooltip message="Self-Finance With Invoice">
            <InvoicingSVGIcon className="size-7 text-approvedBorder" />
          </Tooltip>
        ) : null}
      </div>
    )
  },
  {
    value: "invoiceNumber",
    label: "Invoice #"
  },
  {
    value: "lastInvoiceSentTime",
    label: "Invoice Sent",
    render: (items: string[]) =>
      items.length ? (
        <Tooltip
          position="down"
          message={
            <div className="flex flex-col gap-2">
              <span>Invoice Sent On:</span>
              {items.map((item: string, key: number) => (
                <span key={key}>{dayjs(item).format("MM/DD/YYYY hh:mm:ss A")}</span>
              ))}
            </div>
          }
          wrapperClass="text-approvedBorder">
          <span className="text-approvedBorder">YES</span>
        </Tooltip>
      ) : (
        <span className="text-error">NO</span>
      )
  },
  {
    value: "userLoadNumber",
    label: "Load #",
    rowStyle: "!w-40 !break-all"
  },
  {
    value: "created",
    label: "Requested At",
    render: (item: string) => <span>{dayjs(item).format("MM/DD/YYYY hh:mm A")}</span>
  },
  {
    value: "debtor.discountRate",
    label: "Debtor Rate"
  },
  {
    value: "paymentProfileDiscountRate",
    label: "Carrier %",
    render: (item: number) => <span>{numberFormatter(item)}%</span>
  },
  {
    value: "paymentProfileAmount",
    label: "Carrier Pay",
    render: (item: number) => <span>{formatNumber(item, 2)}</span>
  },
  {
    value: "amountFunded",
    label: "Amt.Funded",
    render: (item: number) => <span>{formatNumber(item, 2)}</span>
  },
  {
    value: "id",
    label: "Details",
    render: (item: string) => (
      <Link
        to={`${routes.HAULPAY_BROKER_FUNDING_REQUEST}/${item}`}
        className="px-4 py-3 border border-primary rounded-base text-primary bg-background">
        More
      </Link>
    )
  }
];

export const ASSIGNED_LOADS_TAB_DATA = [
  {
    label: "All",
    value: "all"
  },
  {
    label: "Incomplete",
    value: "incomplete"
  },
  {
    label: "Pending",
    value: "pending"
  }
];

export const MY_CARRIERS_TABLE_HEADER_LIST = (
  carriers: IFactoringPaymentProfile[],
  onSaveCarrier: (index: number, id: string) => void,
  handleToggleInfoModal: (id: string) => void,
  defaultPaymentSettings: IPaymentSpeed[],
  register: UseFormRegister<Partial<ICarriersFormData>>,
  control: Control<Partial<ICarriersFormData>>,
  getValues: UseFormGetValues<Partial<ICarriersFormData>>,
  setValue: UseFormSetValue<Partial<ICarriersFormData>>
): ITableHeaderList[] => [
  {
    value: "status",
    label: "Status",
    render: (
      item: {
        carrierStatus: string;
        statusDate: string;
        pendingApprovalTasks: string[];
      },
      index: number
    ) => (
      <div className={styles.tooltipGroup}>
        <Tooltip
          message={
            item.pendingApprovalTasks.length === 0 ? (
              <span className="text-sm">No Pending Approval Tasks</span>
            ) : (
              <div className="text-sm text-center">
                <p>Pending Approval Tasks</p>
                <ul className="mt-1.5">
                  {item.pendingApprovalTasks.map((task) => (
                    <li key={task}>- {task}</li>
                  ))}
                </ul>
              </div>
            )
          }
          position={index ? "center" : "down"}>
          <div
            className={clsx(
              "rounded-3xl justify-center px-3 py-1.5 text-xs flex items-center gap-1.5 !bg-transparent",
              COLOR_BY_STATUS_FOR_MY_CARRIER[item.carrierStatus]
            )}>
            {item.carrierStatus === "approved" ? (
              <Icon path={mdiCheckDecagramOutline} className="flex-none size-5" />
            ) : (
              <DataCheckSVGIcon className="flex-none size-5" />
            )}
            {item.carrierStatus.split("_").map((word, index) => (
              <span key={index} className="capitalize">
                {word}{" "}
              </span>
            ))}
          </div>
        </Tooltip>
        <div
          className={clsx(
            styles.tooltipDate,
            "text-sm mt-1 !bg-transparent !border-none tooltip-date"
          )}>
          {dayjs(item.statusDate).format("M/D/YYYY HH:mm A")}
        </div>
      </div>
    )
  },
  {
    value: "payment_profile.owner_email",
    label: "Email",
    rowStyle: "!w-52 !break-all"
  },
  {
    value: "payment_profile.company_name",
    label: "Carrier Name",
    rowStyle: "!w-32",
    render: (item: string, index: number) => (
      <div className="flex flex-col items-start gap-2">
        <button
          type="button"
          className="text-left text-info"
          onClick={() => handleToggleInfoModal(carriers[index].id)}>
          {item}
        </button>
        {carriers[index]?.payment_profile.company_current_factoring_company ? (
          <span className="text-pendingBorder">
            {carriers[index]?.payment_profile.company_current_factoring_company}
          </span>
        ) : null}
      </div>
    )
  },
  {
    value: "payment_profile.owner_phone_number_cell",
    label: "Phone"
  },
  {
    value: "payment_profile.company_mc",
    label: "MC#"
  },
  {
    value: "payment_profile.company_dot",
    label: "DOT#"
  },
  {
    value: "payment_speed",
    label: "Payment Speed",
    render: (_paymentSpeedTerms: any, index: number) => {
      return (
        <PaymentSpeedInput
          noLabel
          skip
          defaultPaymentSpeed={defaultPaymentSettings}
          control={control}
          name={`payment_speed.${index}`}
          customOnChange={(paymentSpeed) => {
            const rate = paymentSpeed.discountRate ?? paymentSpeed.rate ?? 0;
            setValue(
              `carrier_rate.${index}`,
              Number(rate * (100 - Number(getValues(`broker_portion.${index}.value`)))) / 100 + "%"
            );
          }}
          {...(index === 0 ? { direction: "down" } : { direction: "up" })}
        />
      );
    }
  },
  {
    value: "broker_portion",
    label: "Broker Portion",
    render: (_: any, index: number) => (
      <Controller
        name={`broker_portion.${index}`}
        control={control}
        render={({ field: { value, onChange } }: any) => (
          <Select
            value={value}
            disabled={carriers[index]?.factoring_company_relation !== null}
            options={PORTION_PERCENT_LIST}
            onChange={(brokerPortion) => {
              const rate =
                getValues(`payment_speed.${index}`)?.rate ??
                getValues(`payment_speed.${index}`)?.amount;
              rate &&
                setValue(
                  `carrier_rate.${index}`,
                  Number(rate * (100 - Number(brokerPortion.value))) / 100 + "%"
                );
              onChange(brokerPortion);
            }}
            {...(index === 0 ? { direction: "down" } : { direction: "up" })}
          />
        )}
      />
    )
  },
  {
    value: "carrier_rate",
    label: "Carrier Rate",
    render: (value: any, index: number) => (
      <Input
        disabled
        name={`carrier_rate.${index}`}
        register={register}
        inputClass="!border-icon !rounded-lg !p-1.5 desktop:!p-2.5 h-7 desktop:h-10 text-xs desktop:text-sm"
      />
    )
  },
  {
    value: "external_code",
    label: "External Code",
    render: (_: any, index: number) => (
      <Input
        type="text"
        name={`external_code.${index}`}
        register={register}
        inputClass="!border-icon !rounded-lg !p-1.5 desktop:!p-2.5 h-7 desktop:h-10 text-xs desktop:text-sm"
      />
    )
  },
  {
    value: "action",
    label: "Action",
    render: (_: any, index: number, id: string) => (
      <Button className="bg-background" onClick={() => onSaveCarrier(index, id)}>
        Save
      </Button>
    )
  }
];

export const CONNECT_EXISTING_PROFILE_STEP_LIST: IStepItem[] = [
  { id: 1, name: "Add a Carrier", description: "In four easy steps, add a new carrier" },
  { id: 2, name: "Add a Carrier", description: "In four easy steps, add a new carrier" },
  { id: 3, name: "Upload Documents", description: "In four easy steps, add a new carrier" },
  { id: 4, name: "Connection Successful", description: "In four easy steps, add a new carrier" }
];

export const INVITE_CARRIER_STEP_LIST: IStepItem[] = [
  { id: 2, name: "Add a Carrier", description: "In three easy steps, add a new carrier" },
  { id: 3, name: "Upload Documents", description: "In three easy steps, add a new carrier" },
  { id: 4, name: "Connection Successful", description: "In three easy steps, add a new carrier" }
];

export const INVITED_CARRIER_PROFILE_STEP_LIST: IStepItem[] = [
  {
    id: 1,
    name: "Create Carrier Payment Profile",
    description: "In four easy steps, add a new carrier"
  },
  { id: 2, name: "Add a Carrier", description: "In four easy steps, add a new carrier" },
  { id: 3, name: "Upload Documents", description: "In four easy steps, add a new carrier" },
  { id: 4, name: "Payment Profile Create", description: "In four easy steps, add a new carrier" }
];

export const REPORT_TYPE_LIST = [
  {
    id: 0,
    label: "Funding Report",
    value: "funding_report"
  },
  {
    id: 1,
    label: "Carrier Funding Report",
    value: "carrier_funding_report"
  },
  {
    id: 2,
    label: "Broker Funding Report",
    value: "broker_funding_report"
  },
  {
    id: 3,
    label: "AR Report",
    value: "ar_report"
  },
  {
    id: 4,
    label: "Open AR Report",
    value: "open_ar_report"
  },
  {
    id: 5,
    label: "Debtor Aging",
    value: "debtor_aging_by_bucket"
  },
  {
    id: 6,
    label: "Invoice Settlement",
    value: "settlement_report"
  },
  {
    id: 7,
    label: "Chargeback / Giveback Report",
    value: "chargeback_giveback_report"
  },
  {
    id: 8,
    label: "Self-Finance Funding Report",
    value: "self_finance_report"
  },
  {
    id: 9,
    label: "Wallet Transaction Report",
    value: "wallet_transaction"
  }
];

export const FILTER_BY_LIST = [
  {
    id: 0,
    label: "Past 30 days",
    value: "past-30-days"
  }
];

export const EXTRA_DATA: IOption[] = [
  {
    id: 0,
    label: "--",
    value: "none"
  }
];

export const CUSTOMER_TRANS_ID_TYPES: IOption[] = [
  { id: 1, label: "MC", value: "mc" },
  { id: 2, label: "DOT", value: "dot" },
  { id: 3, label: "TAX ID", value: "tax_id" },
  { id: 4, label: "DUNS", value: "duns" }
];

export const FR_LINE_TYPE_LIST: IOption[] = [
  {
    id: 0,
    label: "Freight Charges",
    value: "Freight Charges"
  },
  {
    id: 1,
    label: "Line Haul",
    value: "Line Haul"
  },
  {
    id: 2,
    label: "Fuel Surcharges",
    value: "Fuel Surcharge"
  },
  {
    id: 3,
    label: "Lumper Fee",
    value: "Lumper Fee (Paid by Client)"
  },
  {
    id: 4,
    label: "Gate Charges",
    value: "Gate Charges"
  },
  {
    id: 5,
    label: "Detention",
    value: "Detention"
  },
  {
    id: 6,
    label: "Layover",
    value: "Layover"
  },
  {
    id: 7,
    label: "Storage",
    value: "Storage"
  },
  {
    id: 8,
    label: "TONU",
    value: "TONU"
  },
  {
    id: 9,
    label: "Other",
    value: "Other"
  }
];
export const DUPLICATED_INVOICE_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: "invoice_number",
    label: "Invoice #"
  },
  {
    value: "paymentProfile.company_name",
    label: "Carrier"
  },
  {
    value: "debtor.dba",
    label: "Debtor"
  },
  {
    value: "amount",
    label: "Invoice Amt.",
    render: (item: number) => <span>{item ? formatNumber(item / 100, 2) : "-"}</span>
  },
  {
    value: "linkToInvoice",
    label: "Link to Invoice",
    render: (item: { action: VoidFunction }) => (
      <Button type="button" onClick={item.action}>
        More
      </Button>
    )
  }
];
